import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import { rgba } from 'polished';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { OptionTypeBase, ValueType } from 'react-select';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import useMeasure from 'react-use-measure';
import { v4 } from 'uuid';
import { theme } from '../../../styles';
import { typography } from '../../../styles/typography';
import { FormContext } from '../../../validation/Form';
import type {
  ErrorMessageVariant,
  InputSize,
  InputVariant,
  LabelVariant,
} from '../Input';
import { InsideLabel } from '../Input';
import { ErrorLabel, Label } from '../Label';

export interface IProps {
  options: Array<any>;
  theme?: 'primary' | 'secondary' | 'ternary';
  fill?: boolean;
  onChange: Select['onChange'];
  isOptionDisabled?: (item: OptionTypeBase) => boolean;
  value: any;
  placeholder?: string;
  buildOption: (
    value: any,
    isIsMenu: boolean,
    width: number,
  ) => ValueType<OptionTypeBase, false>;
  label?: string;
  createble?: boolean;
  indicatorPosition?: 'center' | 'right' | 'left';
  onCreateOption?: (val: string) => void;
  formatCreateLabel?: (val: string) => React.ReactNode;
  isValidNewOption?: (val: string) => boolean;
  variant?: InputVariant;
  inputSize?: InputSize;
  isValid?: boolean;
  errorMessage?: string;
  labelVariant?: LabelVariant;
  inputRef?: React.RefObject<HTMLInputElement>;
  errorMessageVariant?: ErrorMessageVariant;
  isDisabled?: boolean;
}

const InputHost = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;

  & > * + * {
    margin-top: 8px;
  }

  &:hover > label {
    color: ${({ theme }) => rgba(theme.shades.primary5 ?? '', 1)};
  }
`;

const InsideLabelVariant = styled(InsideLabel)`
  ${({ isValid, theme, isActive }) =>
    isValid === undefined &&
    `
      color: ${rgba(theme.shades.primary5 ?? '', isActive ? 1 : 0.5)};
    `}
`;

const prefix = 'react-select';

const ReactSelect = styled(Select)<{
  indicatorPosition: string;
  isValid?: boolean;
  inputSize?: InputSize;
  variant?: InputVariant;
  labelVariant?: LabelVariant;
}>`
  .${prefix}__control {
    border-width: 0;
    ${({ variant, theme }) =>
      variant === 'fullBorder' &&
      `box-shadow: 0 0 0 1px
        ${rgba(theme?.colors.foreground ?? '', 0.7)};
        `};

    ${({ variant, theme, currentTheme }) =>
      variant === 'bottomBorder' &&
      `border-bottom: 1px solid
        ${rgba(theme.colors[currentTheme], 0.7)}`};

    ${({ theme, isValid }) =>
      isValid === false && `box-shadow: 0 0 0 2px ${theme?.colors.secondary}`};

    border-radius: 0;
    background-color: ${(props) =>
      props.fill ? rgba(props.theme.colors[props.currentTheme], 0.3) : '#fff'};
    .${prefix}__single-value {
      width: 100%;
      top: initial;
      bottom: ${({ inputSize, labelVariant }) =>
        inputSize !== 'small' && labelVariant === 'insideInput'
          ? '12px'
          : 'inital'};
      transform: none;
      margin-left: 0;
      margin-right: 0;
      text-align: ${({ inputSize }) =>
        inputSize !== 'small' ? 'left' : 'center'};
      color: ${({ theme }) => theme.shades.primary5};
    }
    &::before {
      content: '';
      width: 12px;
      height: 12px;
      background: ${(props) => `linear-gradient(
        to bottom right,
        transparent 0%,
        transparent 50%,
        ${rgba(props.theme.colors[props.currentTheme], 0.3)} 50%,
        ${rgba(props.theme.colors[props.currentTheme], 0.3)} 100%
      )`};
      top: -6px;
      left: ${({ indicatorPosition }) =>
        indicatorPosition === 'right'
          ? `calc(75% - 6px)`
          : indicatorPosition === 'left'
          ? `calc(25% - 6px)`
          : `calc(50% - 6px)`};
      transform: rotate(224deg);
      position: absolute;
      display: ${(props) => (props.fill ? 'block' : 'none')};
    }

    &:active {
      box-shadow: 0 0 0 2px ${({ theme }) => theme.shades.primary3};
    }

    &:hover {
      & .${prefix}__indicator {
        border-color: rgba(30, 61, 90, 1) transparent transparent transparent;
      }
    }
  }
  .${prefix}__menu {
    min-width: 200px;
    text-align: left;
    z-index: 99;
  }

  .${prefix}__indicator-separator {
    display: none;
  }
  .${prefix}__indicator {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    border-color: rgba(
        30,
        61,
        90,
        ${({ value }) => (value !== undefined ? 1 : 0.5)}
      )
      transparent transparent transparent;
    ${({ theme, isValid }) =>
      isValid === false &&
      `border-color: ${theme?.colors.secondary} transparent transparent transparent`};
    padding: 0;
    margin-right: 20px;
  }
  .${prefix}__indicator svg {
    display: none;
  }
  .${prefix}__value-container {
    height: ${({ inputSize }) => (inputSize === 'small' ? '40px' : '64px')};
    padding-left: 15px;
    justify-content: flex-start;
    ${typography.numbers2};
    color: ${({ theme }) => theme.shades.primary5};
  }
  .${prefix}__placeholder {
    color: ${({ theme }) => rgba(theme.shades.primary5 ?? '', 0.5)};
    display: none;
    margin: 0;
    bottom: 12px;
    top: initial;
    transform: none;
    ${(props) =>
      props.isValid === false &&
      `
          color: ${theme?.colors.secondary};
          
        `}
  }
`;

const ReactCreateble = styled(CreatableSelect)`
  .${prefix}__control {
    border: none;

    border-bottom: 1px solid
      ${(props) => props.theme.colors[props.currentTheme]};
    border-radius: 0;
    background-color: ${(props) =>
      props.fill
        ? rgba(props.theme.colors[props.currentTheme], 0.3)
        : 'transparent'};
  }
  .${prefix}__menu {
    min-width: 200px;
    text-align: left;
    z-index: 99;
  }
  .${prefix}__indicator-separator {
    display: none;
  }
  .${prefix}__indicator {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    border-color: rgb(30, 60, 90) transparent transparent transparent;
    padding: 0;
    margin-right: 5px;
  }

  .${prefix}__indicator svg {
    display: none;
  }
  .${prefix}__value-container {
    justify-content: center;
    padding: 8px;
    ${typography.numbers};
    & > div:last-child {
      padding: 0px;
      margin: 0px;
    }
  }
`;

export const Dropdown = forwardRef<any, IProps>(function Ddown(
  {
    options,
    theme = 'primary',
    fill,
    onChange,
    value,
    buildOption,
    placeholder,
    isOptionDisabled,
    label,
    createble,
    onCreateOption,
    formatCreateLabel,
    isValidNewOption,
    indicatorPosition = 'center',
    isValid,
    labelVariant = 'insideInput',
    variant = 'fullBorder',
    inputSize,
    errorMessage,
    errorMessageVariant,
    isDisabled,
  },
  ref,
) {
  const [uuid] = useState(v4());
  const [visited, setVisited] = useState(false);
  const [hostRef, bounds] = useMeasure({ polyfill: ResizeObserver });
  const [selectedValue, setSelectedValue] = useState(value);
  const formContext = useContext(FormContext);
  const showValidState = useMemo(() => {
    return (
      (formContext.validate && !isValid) ||
      (visited && selectedValue !== undefined)
    );
  }, [visited, isValid, selectedValue, formContext.validate]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const labelOrError = useMemo(() => {
    if (errorMessageVariant !== 'insideInput') {
      return label;
    }
    if (isValid === false && showValidState && errorMessage) {
      return errorMessage;
    }
    return label;
  }, [label, errorMessageVariant, errorMessage, isValid, showValidState]);

  return (
    <InputHost ref={hostRef}>
      {label && labelVariant === 'aboveInput' && (
        <Label size={inputSize} htmlFor={uuid}>
          {label}
        </Label>
      )}
      {!createble ? (
        <>
          <ReactSelect
            ref={ref}
            indicatorPosition={indicatorPosition}
            placeholder={
              labelVariant === 'aboveInput' ? placeholder : undefined
            }
            fill={fill}
            currentTheme={theme}
            options={
              options.map((x) => buildOption(x, true, bounds.width)) as any
            }
            classNamePrefix={prefix}
            value={
              selectedValue === undefined
                ? null
                : buildOption(selectedValue, false, bounds.width)
            }
            onChange={(val, meta) => {
              const item = options.find(
                (x) =>
                  buildOption(x, true, bounds.width)?.['value'] ===
                  val?.['value'],
              );

              onChange(val as any, meta as any);
              setSelectedValue(item);
            }}
            inputId={uuid}
            isValid={showValidState ? isValid : undefined}
            isSearchable={false}
            isDisabled={
              isDisabled === undefined ? options.length === 1 : isDisabled
            }
            isOptionDisabled={isOptionDisabled}
            variant={variant}
            onFocus={() => setVisited(true)}
            inputSize={inputSize}
            labelVariant={labelVariant}
            openMenuOnFocus
          />
          {isValid === false &&
            errorMessage &&
            errorMessageVariant === 'belowInput' && (
              <ErrorLabel>{errorMessage}</ErrorLabel>
            )}
        </>
      ) : (
        <ReactCreateble
          inputId={uuid}
          placeholder={placeholder}
          fill={fill}
          currentTheme={theme}
          options={
            options.map((x) => buildOption(x, true, bounds.width)) as any
          }
          classNamePrefix={prefix}
          value={buildOption(selectedValue, false, bounds.width)}
          onChange={(val, meta) => {
            onChange(val as any, meta as any);
          }}
          formatCreateLabel={formatCreateLabel}
          onCreateOption={onCreateOption}
          createOptionPosition="first"
          isValidNewOption={isValidNewOption}
        />
      )}
      {labelVariant === 'insideInput' && labelOrError && (
        <InsideLabelVariant
          htmlFor={uuid}
          isActive={selectedValue !== undefined}
          isValid={showValidState ? isValid : undefined}
        >
          {labelOrError}
        </InsideLabelVariant>
      )}
    </InputHost>
  );
});
